import { TIME_TOLERANCE_MIN } from '../constants/csvColumns';

// "HHMM-HHMM" → decimal hours; handles overnight wrap.
export function calculateHours(timeRange) {
  if (!timeRange || !timeRange.includes('-')) return 0;
  try {
    const [start, end] = timeRange.split('-');
    if (!end || end.trim() === '') return 0;
    const startHour = parseInt(start.substring(0, 2));
    const startMin = parseInt(start.substring(2, 4)) || 0;
    const endHour = parseInt(end.substring(0, 2));
    const endMin = parseInt(end.substring(2, 4)) || 0;

    let hours = endHour - startHour;
    let mins = endMin - startMin;
    if (hours < 0) hours += 24;
    if (mins < 0) { hours -= 1; mins += 60; }
    return hours + mins / 60;
  } catch {
    return 0;
  }
}

// Convert decimal hours to 15-minute unit format (0.15, 0.25, 0.50, 0.75, 1.00 …).
// Boundary rule: 0-7 → 0, 8-22 → 15, 23-37 → 30, 38-52 → 45, 53-60 → 60.
export function formatHoursAsUnits(decimalHours) {
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  let units = 0;
  if (minutes >= 53) units = 100;
  else if (minutes >= 38) units = 75;
  else if (minutes >= 23) units = 50;
  else if (minutes >= 8) units = 25;
  return hours + units / 100;
}

// Minutes since midnight for a "HHMM" string. Returns null on invalid input.
export function parseClockMinutes(timeStr) {
  if (!timeStr || timeStr.length !== 4) return null;
  const hour = parseInt(timeStr.substring(0, 2));
  const min = parseInt(timeStr.substring(2, 4)) || 0;
  if (Number.isNaN(hour) || Number.isNaN(min)) return null;
  let totalMins = hour * 60 + min;
  if (totalMins === 0) totalMins = 1440; // midnight treated as end of day
  return totalMins;
}

// Compare actual vs scheduled visit windows, produce human-readable remark.
export function calculateTimeDifference(actual, scheduled) {
  if (!actual || !scheduled || !actual.includes('-') || !scheduled.includes('-')) {
    return { inDiff: 0, outDiff: 0, remark: 'Missed In/Out' };
  }
  try {
    const [actualIn, actualOut] = actual.split('-');
    const [scheduledIn, scheduledOut] = scheduled.split('-');
    if (!actualIn || !actualOut || !scheduledIn || !scheduledOut ||
        actualIn.trim() === '' || actualOut.trim() === '' ||
        scheduledIn.trim() === '' || scheduledOut.trim() === '') {
      return { inDiff: 0, outDiff: 0, remark: 'Missed In/Out' };
    }

    const parseTime = (time) => {
      const hour = parseInt(time.substring(0, 2));
      const min = parseInt(time.substring(2, 4)) || 0;
      return hour * 60 + min;
    };

    const actualInMins = parseTime(actualIn);
    const scheduledInMins = parseTime(scheduledIn);
    let actualOutMins = parseTime(actualOut);
    let scheduledOutMins = parseTime(scheduledOut);

    // Midnight rollover: treat 0000 as 24:00 when the counterpart is near end-of-day.
    if (actualOutMins === 0 && scheduledOutMins >= 1439) actualOutMins = 1440;
    if (scheduledOutMins === 0 && actualOutMins >= 1439) scheduledOutMins = 1440;

    const inDiff = scheduledInMins - actualInMins;
    const outDiff = scheduledOutMins - actualOutMins;

    let clockInRemark = 'On Time';
    let clockOutRemark = 'On Time';
    let missedMinutes = 0;

    if (Math.abs(inDiff) >= TIME_TOLERANCE_MIN) {
      if (inDiff > 0) { clockInRemark = `Late In by ${inDiff} mins`; missedMinutes += inDiff; }
      else clockInRemark = `Early In by ${Math.abs(inDiff)} mins`;
    }

    const isScheduledNearMidnight = scheduledOutMins >= 1435;
    const isActualNearMidnight = actualOutMins >= 1435 || actualOutMins <= 5;
    if (Math.abs(outDiff) >= TIME_TOLERANCE_MIN && !(isScheduledNearMidnight && isActualNearMidnight)) {
      if (outDiff > 0) clockOutRemark = `Late Out by ${outDiff} mins`;
      else { clockOutRemark = `Early Out by ${Math.abs(outDiff)} mins`; missedMinutes += Math.abs(outDiff); }
    }

    let finalRemark = '';
    if (clockInRemark === 'On Time' && clockOutRemark === 'On Time') {
      finalRemark = 'On Time';
    } else {
      if (clockInRemark !== 'On Time') finalRemark = clockInRemark;
      if (clockOutRemark !== 'On Time') {
        if (finalRemark) finalRemark += ', ';
        finalRemark += clockOutRemark;
      }
    }
    return { inDiff, outDiff, remark: finalRemark || 'On Time', missedMinutes };
  } catch {
    return { inDiff: 0, outDiff: 0, remark: '' };
  }
}

// Detect same-date visit-schedule overlaps for a single patient's visits.
export function detectOverlaps(visits) {
  const overlaps = [];
  const visitsByDate = {};
  visits.forEach((visit, index) => {
    if (!visit.date || !visit.visitSchedule) return;
    (visitsByDate[visit.date] = visitsByDate[visit.date] || []).push({ ...visit, originalIndex: index });
  });

  Object.entries(visitsByDate).forEach(([date, dateVisits]) => {
    for (let i = 0; i < dateVisits.length; i++) {
      for (let j = i + 1; j < dateVisits.length; j++) {
        const a = dateVisits[i];
        const b = dateVisits[j];
        const [sa, ea] = (a.visitSchedule || '').split('-');
        const [sb, eb] = (b.visitSchedule || '').split('-');
        if (!sa || !ea || !sb || !eb) continue;
        const saM = parseClockMinutes(sa);
        const eaM = parseClockMinutes(ea);
        const sbM = parseClockMinutes(sb);
        const ebM = parseClockMinutes(eb);
        if (saM == null || eaM == null || sbM == null || ebM == null) continue;
        const overlapStart = Math.max(saM, sbM);
        const overlapEnd = Math.min(eaM, ebM);
        if (overlapStart < overlapEnd) {
          const overlapMins = overlapEnd - overlapStart;
          if (overlapMins > TIME_TOLERANCE_MIN) {
            overlaps.push({
              visit1Index: a.originalIndex,
              visit2Index: b.originalIndex,
              caregiver1: a.caregiver,
              caregiver2: b.caregiver,
              date,
              overlapMinutes: overlapMins,
            });
          }
        }
      }
    }
  });
  return overlaps;
}
